<script setup lang="ts">
import {BackingInterviewStep} from "@/backing/interfaces/backing";
import BackingInterview from "@/backing/components/BackingInterview.vue";
import {useRouter} from "vue-router";
import {computed} from "vue";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import ConsumerService from "@/consumer/services/ConsumerService";
import {interviewApi} from "@/container";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {usePatientStore} from "@/stores/patient.store";
import {useToastStore} from "@/stores/toast.store";
import useOrganizationSocketEvent from "@/core/composables/useOrganizationSocketEvent";

const router = useRouter();
const patientStore = usePatientStore();
const toastStore = useToastStore();
const props = defineProps<{
    backingId: number;
    steps: BackingInterviewStep[];
    consumerKey: string;
    customerId: number;
    consumer: Customer | Beneficiary;
}>();

/**
 * The patient a link can be handed to: it identifies them by their mobile and
 * birth date, which a beneficiary does not hold on their own.
 */
const customer = computed(() => ConsumerService.isCustomer(props.consumer) ? props.consumer : null);

/**
 * A survey the patient sends from the web link or the app reaches the
 * pharmacy while their backing is open: the lines follow without a reload.
 */
useOrganizationSocketEvent<{ consumer_key: string | null }>('survey-answer-instance.completed', (data) => {
    if (data?.consumer_key === props.consumerKey) {
        patientStore.refresh().catch(() => undefined);
    }
});

/**
 * Redirect to the interview instance view.
 * The interview instance layout is responsible of navigating to the correct view depending the instance state.
 */
const viewInstance = (instanceId: number, step: BackingInterviewStep) => {
    router.push({
        name: 'interview-instances',
        query: {
            backingId: props.backingId,
            consumerKey: props.consumerKey,
            interviewId: step.resource_id,
            interviewInstanceId: instanceId,
            interviewName: step.name,
            customerId: props.customerId
        }
    });
};

const startInstance = (step: BackingInterviewStep) => {
    router.push({
        name: 'interview-instances',
        query: {
            backingId: props.backingId,
            interviewId: step.resource_id,
            consumerKey: props.consumerKey,
            customerId: props.customerId,
            interviewName: step.name
        }
    });
};

/**
 * Start a new, empty auto-evaluation once the patient's is sent: the API
 * makes the pharmacist's instance the latest, the one the patient's links and
 * app resume. The pharmacist stays on the backing to hand them a link.
 */
const restartInstance = async (step: BackingInterviewStep) => {
    try {
        await interviewApi().createInstance(step.resource_id, props.consumerKey, props.backingId);
        toastStore.showSuccess({content: "L'auto-évaluation est à remplir à nouveau par le patient."});
        await patientStore.refresh();
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    }
};
</script>

<template>
  <div class="px-4 rounded-lg border-primary divide-y divide-gray-200" style="border-width: 1px">
    <BackingInterview
      v-for="step in steps"
      :key="step.resource_id"
      :step="step"
      :backing-id="backingId"
      :customer="customer"
      @view-instance="instanceId => viewInstance(instanceId, step)"
      @start-instance="startInstance(step)"
      @restart-instance="restartInstance(step)"
    />
    <p v-if="!steps.length" class="py-4 text-center">
      Aucun autre entretien n'est disponible
    </p>
  </div>
</template>

<style scoped>

</style>
